import axios, { AxiosError, type InternalAxiosRequestConfig } from "axios";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:3010";

/** Keys the panel keeps in localStorage. Collected so nothing goes stale. */
export const storage = {
  accessToken: "cp.accessToken",
  refreshToken: "cp.refreshToken",
  activeOrgId: "cp.activeOrgId",
  organizations: "cp.organizations",
  email: "cp.email",
} as const;

export const api = axios.create({ baseURL: API_BASE });

/**
 * Attaches the token and the active organisation.
 *
 * `X-Org-Id` goes on every request because a user may belong to several
 * organisations; the server checks membership each time. Leaving it off is
 * what a 400 `ORG_REQUIRED` means.
 */
api.interceptors.request.use((config) => {
  const token = localStorage.getItem(storage.accessToken);
  if (token) config.headers.Authorization = `Bearer ${token}`;

  const orgId = localStorage.getItem(storage.activeOrgId);
  if (orgId) config.headers["X-Org-Id"] = orgId;

  return config;
});

interface RetryConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

/**
 * Refresh-on-401.
 *
 * A single in-flight refresh is shared by every request that hits a 401 at the
 * same time. Two parallel `/auth/refresh` calls would rotate the token twice,
 * and the server treats a replayed refresh token as a leak and revokes the
 * entire session family — logging the user out for being *too* active.
 */
let refreshInFlight: Promise<string> | null = null;

async function performRefresh(): Promise<string> {
  const refreshToken = localStorage.getItem(storage.refreshToken);
  if (!refreshToken) throw new Error("NO_REFRESH_TOKEN");

  // Raw axios: the interceptors above would add a stale bearer and the one
  // below would loop us straight back into a refresh.
  const response = await axios.post(`${API_BASE}/auth/refresh`, { refreshToken });
  const data = response.data as { accessToken: string; refreshToken?: string };
  localStorage.setItem(storage.accessToken, data.accessToken);
  if (data.refreshToken) localStorage.setItem(storage.refreshToken, data.refreshToken);
  return data.accessToken;
}

export function clearSession() {
  Object.values(storage).forEach((key) => localStorage.removeItem(key));
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetryConfig | undefined;
    const url = original?.url ?? "";

    // Let the login and refresh endpoints report their own failures, or a
    // wrong password would silently turn into a redirect.
    const isAuthEndpoint = url.includes("/auth/login") || url.includes("/auth/refresh");

    if (error.response?.status !== 401 || !original || original._retry || isAuthEndpoint) {
      return Promise.reject(error);
    }

    original._retry = true;
    try {
      refreshInFlight ??= performRefresh().finally(() => {
        refreshInFlight = null;
      });
      const token = await refreshInFlight;
      original.headers.Authorization = `Bearer ${token}`;
      return api.request(original);
    } catch {
      clearSession();
      // Full reload rather than a router navigate: it guarantees no stale
      // query cache or component state survives the logout.
      window.location.href = "/login";
      return Promise.reject(error);
    }
  }
);

/** Pulls the server's `{ error: { code, message } }` out of an axios failure. */
export function apiErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const payload = error.response?.data as
      | { error?: { code?: string; message?: string } }
      | undefined;
    if (payload?.error?.message) return payload.error.message;
    if (!error.response) return fallback;
  }
  return fallback;
}

export function apiErrorCode(error: unknown): string | null {
  if (!axios.isAxiosError(error)) return null;
  const payload = error.response?.data as { error?: { code?: string } } | undefined;
  return payload?.error?.code ?? null;
}
